// Shared animation variants for consistent page transitions
// Use these across all pages to ensure consistent UX
// OPTIMIZED: Ultra-snappy animations - total animation time under 200ms
// - Individual transitions: 120ms
// - Stagger delay: 25ms (page sections), 15ms (KPI cards)
// - Y offset: 8px (subtle but noticeable)

import { Variants, Transition } from 'framer-motion'

// Ultra-snappy transition - content visible almost immediately
const snappyTransition: Transition = {
  duration: 0.12, // 120ms - fast but still smooth
  ease: [0.25, 0.1, 0.25, 1], // Custom easeOut
}

const mediumTransition: Transition = {
  duration: 0.15,
  ease: [0.25, 0.1, 0.25, 1],
}

/**
 * Fade in and slide up animation for individual elements
 * Use with motion.div variants={fadeInUp}
 * OPTIMIZED: Minimal y offset (8px) for subtle but fast effect
 */
export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: snappyTransition,
  },
}

/**
 * Stagger children animation for lists and grids
 * Use on parent container: variants={stagger}
 * Children should use variants={fadeInUp}
 * OPTIMIZED: 25ms stagger - fast cascade, total under 200ms for 4-5 items
 */
export const stagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.025,
      delayChildren: 0,
    },
  },
}

/**
 * Fast stagger for KPI cards - 15ms between items
 * 5 cards * 15ms = 75ms total stagger + 120ms animation = 195ms < 200ms
 */
export const fastStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.015,
      delayChildren: 0,
    },
  },
}

/**
 * Page container animation props
 * Use on the root motion.div of each page
 *
 * Example:
 * <motion.div {...pageContainer} className="space-y-6">
 */
export const pageContainer = {
  initial: 'hidden',
  animate: 'visible',
  variants: stagger,
}

/**
 * Simple fade in animation for pages without stagger
 * Use when you don't need staggered children
 * OPTIMIZED: Minimal y offset (8px) for fast, subtle effect
 *
 * Example:
 * <motion.div {...simpleFadeIn} className="space-y-6">
 */
export const simpleFadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: snappyTransition,
  },
}

/**
 * Animation for collapsible/expandable content
 * OPTIMIZED: Faster collapse/expand
 */
export const expandCollapse: Variants = {
  hidden: { opacity: 0, height: 0, transition: mediumTransition },
  visible: { opacity: 1, height: 'auto', transition: mediumTransition },
}

/**
 * Instant fade for skeleton replacement
 * Used when content replaces skeleton loaders
 */
export const instantFade: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.1, ease: 'easeOut' },
  },
}

/**
 * Scale up animation for cards appearing
 */
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: snappyTransition,
  },
}
